/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";

import {AuthenticationService} from './authentication.service';
import {BaseService} from "./base-service";

import {PropertyPatch} from '../models/property-patch';
import {Service} from '../models/service';
import {ServiceInstance} from '../models/service-instance';
import { PagedResults } from 'app/models/paged-results';
import { RestService } from './rest-service';
import { Url_Base } from 'app/rest-constants';


@Injectable()
export class ServiceService extends BaseService {


  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService
  ) {
    super(router, authenticationService);
  }

  private getUrl(): string {
    return `${Url_Base}/services/`;
  }

  getNumberServices(): Promise<Number> {
    const callUrl = this.getUrl() + 'count';
    return this.mRestService.get<Number>(callUrl);
  }

  getNumberServiceInstances(): Promise<Number> {
    const callUrl = this.getUrl() + 'instances/count';
    return this.mRestService.get<Number>(callUrl);
  }

  getAllServicesForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<Service>> {
    const callUrl = this.getUrl() + 'paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<Service>>(callUrl);
  }

  getAllServiceInstancesForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<ServiceInstance>> {
    const callUrl = this.getUrl() + 'instances/paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<ServiceInstance>>(callUrl);
  }

  getAllServices(): Promise<Service[]> {
    const callUrl = this.getUrl();
    return this.mRestService.get<Service[]>(callUrl);
  }

  getServiceById(id: number): Promise<Service> {
    const callUrl = this.getUrl() + `${id}`;
    return this.mRestService.get<Service>(callUrl);
  }

  createService(service: Service): Promise<Service> {
    const callUrl = this.getUrl();
    return this.mRestService.post<Service>(callUrl, service);
  }

  updateService(service: Service): Promise<Service> {
    const callUrl = this.getUrl();
    return this.mRestService.put<Service>(callUrl, service);
  }

  deleteServices(serviceIds: number[]): Promise<void> {
    const callUrl = this.getUrl();
    return this.mRestService.delete(callUrl, serviceIds);
  }






  getAllServiceInstances(): Promise<ServiceInstance[]> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.get<ServiceInstance[]>(callUrl);
  }

  getServiceInstancesFiltered(start: number, size: number, order: string, filterString: string): Promise<ServiceInstance[]> {
    const url = this.getUrl() + `instances/filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<ServiceInstance[]>(url);
  } 

  getAllServiceInstancesOfServiceType(serviceTypeUuid: string): Promise<ServiceInstance[]> {
    const callUrl = `${this.getUrl()}uuid/${serviceTypeUuid}/instances`;
    return this.mRestService.get<ServiceInstance[]>(callUrl);
  }

  getServiceInstanceById(id: number | string): Promise<ServiceInstance> {
    const callUrl = this.getUrl() + `instances/${id}`;
    return this.mRestService.get<ServiceInstance>(callUrl);
  }

  getServiceInstanceByUuid(uuid: string): Promise<ServiceInstance> {
    const callUrl = this.getUrl() + `instances/uuid/${uuid}`;
    return this.mRestService.get<ServiceInstance>(callUrl);
  }

  createServiceInstance(serviceInstance: ServiceInstance): Promise<ServiceInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.post<ServiceInstance>(callUrl, serviceInstance);
  }

  updateServiceInstance(serviceInstance: ServiceInstance): Promise<ServiceInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.put<ServiceInstance>(callUrl, serviceInstance);
  }

  deleteServiceInstances(serviceInstanceIds: number[]): Promise<any> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.delete(callUrl, serviceInstanceIds);
  }

  /**
  * Resets the Service Instance's Driver and recreates it.
  */
  resetServiceDriver(id: number): Promise<void> {
    console.log(`Reset Service Driver\n\tID: '${id}`);
    const callUrl = `${this.getUrl()}instances/${id}/reset-driver`;
    return this.mRestService.post(callUrl, null);
  }

  executeFunction(serviceInstanceId: number, functionId: number, propertyPatches: PropertyPatch[], responseType?: string): Promise<any> {
    const callUrl = this.getUrl() + `instances/${serviceInstanceId}/execute-function?function_id=${functionId}`;
    return this.mRestService.post(callUrl, propertyPatches, responseType);
  }

  executeFunctionByKey(serviceInstanceId: number | string, functionKey: string, propertyPatches: PropertyPatch[], responseType?: string): Promise<any> {
    const callUrl = this.getUrl() + `instances/${serviceInstanceId}/execute-function?function_key=${functionKey}`;
    return this.mRestService.post(callUrl, propertyPatches, responseType);
  }

}
